import { defineStore } from "pinia";

import chatProvidersApi from "@/services/api/chatProvidersApi";

import { useNotificationStore } from "@/stores/notificationStore";
import { getCompanyId } from "@/utils/userUtils";

export const useChatProviderStore = defineStore("chatProvider", {
  state: () => ({
    chatProviders: [],
    config: {},
    companyId: getCompanyId(),
    dataIsLoading: false,
    isSubmitting: false,
  }),

  getters: {
    maxBotProviders: (state) =>
      state.chatProviders.filter(
        (provider) => provider.provider_type === "max_bot"
      ),
    maxBotWebhookUrl: (state) => state.config.max_bot_webhook_url || "",
  },

  actions: {
    normalizeResponseData(response) {
      return response.data.result || response.data || [];
    },

    async fetchChatProviders() {
      this.dataIsLoading = true;
      try {
        const [response] = await Promise.all([
          chatProvidersApi.index(this.companyId),
          this.fetchChatProviderConfig(),
        ]);
        this.chatProviders = this.normalizeResponseData(response);
      } finally {
        this.dataIsLoading = false;
      }
    },

    async fetchChatProviderConfig() {
      const response = await chatProvidersApi.connectionConfig(this.companyId);
      this.config = this.normalizeResponseData(response);
    },

    async createChatProvider(payload) {
      this.isSubmitting = true;
      try {
        const response = await chatProvidersApi.create(
          this.companyId,
          payload
        );
        const provider = this.normalizeResponseData(response);
        this.chatProviders.unshift(provider);
        useNotificationStore().showSuccess("Чат-бот успешно добавлен");
        return provider;
      } finally {
        this.isSubmitting = false;
      }
    },

    async updateChatProvider(id, payload) {
      this.isSubmitting = true;
      try {
        const response = await chatProvidersApi.update(
          this.companyId,
          id,
          payload
        );
        const provider = this.normalizeResponseData(response);
        this.replaceChatProvider(provider);
        useNotificationStore().showSuccess("Чат-бот успешно обновлен");
        return provider;
      } finally {
        this.isSubmitting = false;
      }
    },

    async enableChatProvider(id) {
      this.isSubmitting = true;
      try {
        const response = await chatProvidersApi.enable(this.companyId, id);
        const provider = this.normalizeResponseData(response);
        this.replaceChatProvider(provider);
        useNotificationStore().showSuccess("Чат-бот включен");
        return provider;
      } finally {
        this.isSubmitting = false;
      }
    },

    async disableChatProvider(id) {
      this.isSubmitting = true;
      try {
        const response = await chatProvidersApi.disable(this.companyId, id);
        const provider = this.normalizeResponseData(response);
        this.replaceChatProvider(provider);
        useNotificationStore().showSuccess("Чат-бот отключен");
        return provider;
      } finally {
        this.isSubmitting = false;
      }
    },

    async checkChatProviderWebhook(id) {
      this.isSubmitting = true;
      try {
        const response = await chatProvidersApi.checkWebhook(this.companyId, id);
        const result = this.normalizeResponseData(response);
        await this.fetchChatProviders();
        useNotificationStore().showSuccess(result.message);
        return result;
      } finally {
        this.isSubmitting = false;
      }
    },

    async toggleChatProvider(provider) {
      if (provider.enabled) {
        return this.disableChatProvider(provider.id);
      }

      return this.enableChatProvider(provider.id);
    },

    replaceChatProvider(provider) {
      const index = this.chatProviders.findIndex(
        (item) => item.id === provider.id
      );

      if (index === -1) {
        this.chatProviders.unshift(provider);
      } else {
        this.chatProviders.splice(index, 1, provider);
      }
    },
  },
});
